import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import urls from "../../config/urls.json";
import { useSession } from "../../hooks/useSession";
import { useStates } from "../../hooks/useStates";
import { ApiService as api } from "../../services/ApiService";
import { getYMD } from "../../services/formatters";
import { isCf } from "../../services/validators";
import { useDynatrace } from "../../hooks/useDynatrace";
import { useEditAppointment } from "../../hooks/useEditAppointment";

export const useFilialiDirectPage = () => {
    const navigate = useNavigate();
    const { read, update, batchUpdate, canISkipCfPage, setC5 } = useSession();
    const { states, setCurrentState, isDatiUtenteReady, isCfFetchStart, isCfFetchError, isFilialiSlideBusy } = useStates();
    const { askForEditAppointment, showModal, closeModal } = useEditAppointment();

    const [fetchError, setFetchError] = useState(false);
    const [fetchErrorMessage, setFetchErrorMessage] = useState("");

    let daySlider = useRef();

    useEffect(() => {
        setCurrentState(states.LOAD_FILIALI_SLIDES_END);
        update("codiceFiscale", "");
    }, []);

    const {} = useDynatrace();

    const next = () => {
        if (canISkipCfPage()) {
            setCurrentState(states.FETCH_CF_END);
            if (true == read("ricontattoCf") && true == read("ricontattoLuogo")) {
                update("ricontatto", true);
                navigate(urls.clink.path);
            } else if (true == read("utenteRiconosciuto")) {
                navigate(urls.datiPersonaliRiconosciuto.path);
            } else {
                navigate(urls.datiPersonaliNonRiconosciuto.path);
            }
        } else {
            navigate(urls.codiceFiscale.path);
        }
    };

    const onGiorniSlideChange = async (giornoIdx) => {
        const isCfAlreadyOk = read("state") >= states.FETCH_CF_END;

        setCurrentState(states.LOAD_FILIALI_SLIDES_START);
        update("dataScelta", read("disponibilita")[giornoIdx]);

        // call new Disponibilita
        let disponibilitaResult = {};
        try {
            const TipoFiliali = read("prodotto") == 3 ? 1 : 0;
            disponibilitaResult = await api.Disponibilita({
                CodFiliale: read("filialeScelta").codFiliale,
                Giorno: getYMD(read("dataScelta").data),
                TipoFiliali,
            });
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore nel caricamento delle disponibilità delle filiali. <br />Per favore riprova.");
            setFetchError(true);
            return;
        }

        update("orari", disponibilitaResult.orariDisponibili);

        let favouriteHour = disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true)[0];
        let eligibleOrarioScelto = [];
        if (read("orarioScelto")) {
            eligibleOrarioScelto = disponibilitaResult.orariDisponibili.filter((_) => _.disponibile == true && _.hour == read("orarioScelto").hour && _.minute == read("orarioScelto").minute);
        }
        if (eligibleOrarioScelto.length > 0) {
            favouriteHour = eligibleOrarioScelto[0];
        }

        update("orarioScelto", favouriteHour);
        setCurrentState(states.LOAD_FILIALI_SLIDES_END);

        if (isCfAlreadyOk) {
            setCurrentState(states.FETCH_CF_END);
        }
    };

    const onCfChange = async (value) => {
        setCurrentState(states.BUSY);
        update("codiceFiscale", value);
        update("codiceFiscale_external", value);

        if (value.length < 16) return;

        if (!isCf(value)) {
            setCurrentState(states.FETCH_CF_ERROR);
            return;
        }

        setCurrentState(states.FETCH_CF_START);

        let cfResult = {};
        try {
            cfResult = await api.GetUserDataAndAppointmentByCf({
                CodiceFiscale: value,
            });
        } catch (error) {
            setFetchErrorMessage("C'è stato un errore con il codice fiscale inserito. <br />Per favore riprova.");
            setFetchError(true);
            setCurrentState(states.LOAD_FILIALI_SLIDES_END);
            return;
        }

        // let's see if he has an appointment already
        update("oppID", "");

        // WAF...
        if (!cfResult.hasOwnProperty("appointmentData")) {
            setFetchErrorMessage("C'è stato un errore con il codice fiscale inserito. <br />Per favore riprova.");
            setFetchError(true);
            setCurrentState(states.LOAD_FILIALI_SLIDES_END);
            return;
        }

        if (cfResult.appointmentData && !!cfResult.appointmentData.appointmentId) {
            setCurrentState(states.FETCH_CF_EDIT_APPOINTMENT);
            askForEditAppointment();
            return;
        }

        update("ricontattoCf", cfResult.ricontatto);

        const utente = cfResult.datiCliente || {};
        const mergeableFields = {
            utenteRiconosciuto: !!utente.codiceFiscale,
        };
        mergeableFields["nome"] = utente.nome || "";
        mergeableFields["cognome"] = utente.cognome || "";
        mergeableFields["email"] = utente.email || "";
        mergeableFields["cellulare"] = utente.cellulare || utente.telefono || "";
        mergeableFields["capResidenza"] = utente.capResidenza || utente.cap || "";

        batchUpdate(mergeableFields);
        setCurrentState(states.FETCH_CF_END);
    };

    const getCfInputState = () => {
        return isCfFetchError() ? 4 : isCf(read("codiceFiscale")) ? 3 : 1;
    };

    return {
        next,
        read,
        update,
        daySlider,
        onGiorniSlideChange,
        isFilialiSlideBusy,
        fetchErrorMessage,
        fetchError,
        onCfChange,
        setFetchError,
        isDatiUtenteReady,
        isCfFetchStart,
        getCfInputState,
        showModal,
        closeModal,
    };
};
